// auth.interceptor.ts
import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap, finalize } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';

/**
 * Interceptor HTTP para tratamento de tokens e erros
 * Seguindo as regras do Angular: injeção com inject(), tratamento robusto de erros
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly authService = inject(AuthService);
    private readonly router = inject(Router);

    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    // URLs que não precisam de autenticação
    private readonly publicUrls = [
        '/api/auth/login',
        '/api/auth/refresh',
        '/api/auth/forgot-password',
        '/api/auth/validate-reset-token',
        '/api/auth/reset-password',
        '/api/posts/public/', // Endpoint público para validação de código de registro
        '/api/users/public/post-supervisor', // Criação pública de post supervisor
        '/api/users/public/post-resident', // Criação pública de post resident
        // URLs sem /api (quando proxy remove o prefixo)
        'auth/login',
        'auth/refresh',
        'auth/forgot-password',
        'auth/validate-reset-token',
        'auth/reset-password',
        'posts/public/', // Endpoint público para validação de código de registro
        'users/public/post-supervisor', // Criação pública de post supervisor
        'users/public/post-resident', // Criação pública de post resident
    ];

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Verificar se é URL pública
        if (this.isPublicUrl(request.url)) {
            return next.handle(request);
        }

        // Adicionar token de autenticação
        const authRequest = this.addAuthenticationToken(request);

        return next.handle(authRequest).pipe(
            catchError((error: HttpErrorResponse) => {
                return this.handleError(error, request, next);
            })
        );
    }

    /**
     * Adiciona token de autenticação ao request
     * @param request Request HTTP
     * @returns Request com token
     */
    private addAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
        const token = this.authService.obterTokenAcesso();

        if (token) {
            // Para FormData, não definir Content-Type (deixar browser definir com boundary)
            const isFormData = request.body instanceof FormData;
            const headers: any = {
                Authorization: `Bearer ${token}`,
            };

            // Só definir Content-Type se não for FormData
            if (!isFormData) {
                headers['Content-Type'] = 'application/json';
            }

            return request.clone({
                setHeaders: headers,
            });
        }

        return request;
    }

    /**
     * Trata erros HTTP
     * @param error Erro HTTP
     * @param request Request original
     * @param next Handler
     * @returns Observable com erro tratado
     */
    private handleError(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Tratar erro 401 (token expirado)
        if (error.status === 401) {
            return this.handle401Error(request, next);
        }

        // Tratar erro 403 (sem permissão)
        if (error.status === 403) {
            return this.handle403Error(error);
        }

        // Tratar erro 404 (recurso não encontrado)
        if (error.status === 404) {
            return this.handle404Error(error);
        }

        // Tratar erro 422 (validação)
        if (error.status === 422) {
            return this.handle422Error(error);
        }

        // Tratar erro 500 (servidor)
        if (error.status >= 500) {
            return this.handle500Error(error);
        }

        // Log do erro para debug
        console.error('Erro HTTP:', error);

        return throwError(() => error);
    }

    /**
     * Trata erro 401 (não autorizado)
     * Tenta renovar token automaticamente
     */
    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return this.authService.renovarToken().pipe(
                switchMap(() => {
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(true);

                    // Reenviar request com novo token
                    const authRequest = this.addAuthenticationToken(request);
                    return next.handle(authRequest);
                }),
                catchError(error => {
                    this.isRefreshing = false;
                    this.redirectToLogin();
                    return throwError(() => error);
                })
            );
        }

        // Se já está renovando, aguardar
        return this.refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap(() => {
                const authRequest = this.addAuthenticationToken(request);
                return next.handle(authRequest);
            })
        );
    }

    /**
     * Trata erro 403 (sem permissão)
     */
    private handle403Error(error: HttpErrorResponse): Observable<never> {
        console.error('Acesso negado:', error);

        // Mostrar mensagem de erro específica
        const errorMessage = this.extractErrorMessage(error) || 'Você não tem permissão para acessar este recurso';

        // Aqui você pode mostrar um toast ou notificação
        // this.notificationService.showError(errorMessage);

        return throwError(() => new Error(errorMessage));
    }

    /**
     * Trata erro 404 (não encontrado)
     */
    private handle404Error(error: HttpErrorResponse): Observable<never> {
        console.error('Recurso não encontrado:', error);

        const errorMessage = this.extractErrorMessage(error) || 'Recurso não encontrado';

        return throwError(() => new Error(errorMessage));
    }

    /**
     * Trata erro 422 (validação)
     */
    private handle422Error(error: HttpErrorResponse): Observable<never> {
        console.error('Erro de validação:', error);

        const errorMessage = this.extractErrorMessage(error) || 'Dados inválidos';

        // Aqui você pode processar erros de validação específicos
        if (error.error?.errors) {
            const validationErrors = this.formatValidationErrors(error.error.errors);
            return throwError(() => new Error(validationErrors));
        }

        return throwError(() => new Error(errorMessage));
    }

    /**
     * Trata erro 500+ (servidor)
     */
    private handle500Error(error: HttpErrorResponse): Observable<never> {
        console.error('Erro do servidor:', error);

        const errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';

        // Aqui você pode mostrar um toast ou notificação
        // this.notificationService.showError(errorMessage);

        return throwError(() => new Error(errorMessage));
    }

    /**
     * Redireciona para login
     */
    private redirectToLogin(): void {
        this.authService.executarLogout().subscribe({
            complete: () => {
                this.router.navigate(['/auth/login']);
            },
        });
    }

    /**
     * Verifica se URL é pública
     * @param url URL a ser verificada
     * @returns Boolean
     */
    private isPublicUrl(url: string): boolean {
        return this.publicUrls.some(publicUrl => {
            // Verificar se a URL contém o endpoint público
            return url.includes(publicUrl) || url.endsWith(publicUrl);
        });
    }

    /**
     * Extrai mensagem de erro da resposta
     * @param error Erro HTTP
     * @returns Mensagem de erro
     */
    private extractErrorMessage(error: HttpErrorResponse): string | null {
        if (error.error?.message) {
            return error.error.message;
        }

        if (error.error?.error) {
            return error.error.error;
        }

        if (typeof error.error === 'string') {
            return error.error;
        }

        return null;
    }

    /**
     * Formata erros de validação
     * @param errors Erros de validação
     * @returns Mensagem formatada
     */
    private formatValidationErrors(errors: any): string {
        if (Array.isArray(errors)) {
            return errors.join(', ');
        }

        if (typeof errors === 'object') {
            const messages: string[] = [];

            for (const field in errors) {
                if (errors.hasOwnProperty(field)) {
                    const fieldErrors = errors[field];
                    if (Array.isArray(fieldErrors)) {
                        messages.push(...fieldErrors);
                    } else {
                        messages.push(fieldErrors);
                    }
                }
            }

            return messages.join(', ');
        }

        return errors.toString();
    }
}

/**
 * Interceptor para adicionar loading automático
 */
@Injectable()
export class LoadingInterceptor implements HttpInterceptor {
    private activeRequests = 0;
    private readonly loadingSubject = new BehaviorSubject<boolean>(false);

    readonly loading$ = this.loadingSubject.asObservable();

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Ignorar requests específicos
        if (request.headers.get('X-Skip-Loading') === 'true') {
            return next.handle(request);
        }

        this.activeRequests++;
        this.updateLoadingState();

        return next.handle(request).pipe(
            finalize(() => {
                this.activeRequests--;
                this.updateLoadingState();
            })
        );
    }

    private updateLoadingState(): void {
        this.loadingSubject.next(this.activeRequests > 0);
    }
}
